import { Checkbox, Popover, Tooltip } from "@shopify/polaris"
import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import {
	canModifyKopyPagesThemeColors,
	getAutostorePagesThemeName,
	isAutostoreDefaultThemeSelection,
} from "../../utils/kopyPagesThemes.js"

export const ThemeColorPalettePicker = ({
	disabled,
	colorPalette,
	onPaletteSelected,
	themeIdSelected,
	modifyThemeColors,
	setModifyThemeColors,
}) => {
	const { t } = useTranslation()
	const { state } = useContext(GlobalStateContext)
	const [themeColorPalettePopoverActive, setThemeColorPalettePopoverActive] =
		useState(false)

	const togglePopover = (e) => {
		e.stopPropagation()
		setThemeColorPalettePopoverActive(!themeColorPalettePopoverActive)
	}

	// Get current theme's detected palette
	const selectedTheme = state.storeThemes?.find(
		(theme) => theme.id === themeIdSelected,
	)
	const autostoreDefaultThemeName = getAutostorePagesThemeName(selectedTheme)
	const isAutostoreDefaultSelected =
		isAutostoreDefaultThemeSelection(colorPalette)
	const showAutostoreDefaultOption = Boolean(
		autostoreDefaultThemeName || isAutostoreDefaultSelected,
	)
	const autostoreDefaultOptionValue =
		autostoreDefaultThemeName || colorPalette
	const palette =
		themeColorPalettes[colorPalette] ||
		themeColorPalettes[selectedTheme?.themePalette] ||
		themeColorPalettes.pink
	const themeCurrentPalette = selectedTheme?.themePalette || null
	const isPaletteChanged = showAutostoreDefaultOption
		? !isAutostoreDefaultSelected
		: themeCurrentPalette && themeCurrentPalette !== colorPalette
	const canModifySelectedThemeColors =
		canModifyKopyPagesThemeColors(selectedTheme)

	return (
		<>
			<div
				style={{
					// background: "#f6cee8",
					// color: "#c91c7d",
					// background: colorPalette.productSectionBg,
					background: palette.pageBg,
					color: palette.pageText,
					borderRadius: "0.3rem",
					display: "flex",
					gap: "0.2rem",
					fontSize: "0.75rem",
					cursor: "pointer",
					width: "fit-content",
				}}
				onClick={(e) => {
					if (!disabled) {
						togglePopover(e)
					}
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						padding: "0.1rem 0.3rem",
						paddingRight: "0.1rem",
						borderRadius: "0.3rem 0rem 0rem 0.3rem",
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="0.9rem"
						height="0.9rem"
						viewBox="0 0 512 512"
					>
						<path
							fill="none"
							stroke="currentColor"
							strokeMiterlimit="10"
							strokeWidth="32"
							d="M430.11 347.9c-6.6-6.1-16.3-7.6-24.6-9c-11.5-1.9-15.9-4-22.6-10c-14.3-12.7-14.3-31.1 0-43.8l30.3-26.9c46.4-41 46.4-108.2 0-149.2c-34.2-30.1-80.1-45-127.8-45c-55.7 0-113.9 20.3-158.8 60.1c-83.5 73.8-83.5 194.7 0 268.5c41.5 36.7 97.5 55 152.9 55.4h1.7c55.4 0 110-17.9 148.8-52.4c14.4-12.7 11.99-36.6.1-47.7Z"
						/>
						<circle cx="144" cy="208" r="32" fill="currentColor" />
						<circle cx="152" cy="311" r="32" fill="currentColor" />
						<circle cx="224" cy="144" r="32" fill="currentColor" />
						<circle cx="256" cy="367" r="48" fill="currentColor" />
						<circle cx="328" cy="144" r="32" fill="currentColor" />
					</svg>
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.2rem",
						padding: "0.1rem 0.3rem",
						paddingLeft: "0rem",
					}}
				>
					<span>{t("kopy_pages.theme_colors")}</span>
					<div style={{ height: "0.85rem", width: "0.85rem" }}>
						<svg
							width={"0.9rem"}
							height={"0.9rem"}
							viewBox="0 0 512 512"
						>
							<path
								d="M128 192l128 128 128-128z"
								fill="currentColor"
							></path>
						</svg>
					</div>
				</div>
			</div>

			<Popover
				active={themeColorPalettePopoverActive}
				activator={<div></div>}
				onClose={() => setThemeColorPalettePopoverActive(false)}
			>
				<div
					style={{
						padding: "0.3rem",
						display: "flex",
						flexDirection: "column",
						gap: "0.25rem",
					}}
				>
					<style>{`
						.theme-color-palette-option:hover {
							background: #f0f0f0;
						}
					`}</style>
					{isPaletteChanged && canModifySelectedThemeColors && (
						<div
							style={{
								paddingLeft: "0.25rem",
								paddingBottom: "0.3rem",
								marginBottom: "0.3rem",
								borderBottom: "1px solid #e0e0e0",
							}}
						>
							<Checkbox
								label={
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "0.3rem",
										}}
									>
										<p style={{ fontSize: "0.85rem" }}>
											{t(
												"kopy_pages.modify_theme_colors",
											)}
										</p>
										<Tooltip
											content={
												<div
													style={{
														padding: "0.5rem",
													}}
												>
													{t(
														"kopy_pages.modify_theme_colors_tooltip",
													)}
												</div>
											}
										>
											<span
												style={{
													display: "inline-flex",
													cursor: "help",
												}}
											>
												<svg
													width="0.9rem"
													height="0.9rem"
													viewBox="0 0 24 24"
													aria-label="Info"
												>
													<title>Info</title>
													<path
														fill="currentColor"
														d="M11.95 18q.525 0 .888-.363t.362-.887t-.362-.888t-.888-.362t-.887.363t-.363.887t.363.888t.887.362m-.9-3.85h1.85q0-.825.188-1.3t1.062-1.3q.65-.65 1.025-1.238T15.55 8.9q0-1.4-1.025-2.15T12.1 6q-1.425 0-2.312.75T8.55 8.55l1.65.65q.125-.45.563-.975T12.1 7.7q.8 0 1.2.438t.4.962q0 .5-.3.938t-.75.812q-1.1.975-1.35 1.475t-.25 1.825M12 22q-2.075 0-3.9-.787t-3.175-2.138T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20m0-8"
													></path>
												</svg>
											</span>
										</Tooltip>
									</div>
								}
								checked={modifyThemeColors}
								onChange={() =>
									setModifyThemeColors(!modifyThemeColors)
								}
							/>
						</div>
					)}
					{showAutostoreDefaultOption ? (
						<div
							className="theme-color-palette-option"
							onClick={() =>
								onPaletteSelected(autostoreDefaultOptionValue)
							}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "0.5rem",
								cursor: "pointer",
								padding: "0.2rem",
								paddingRight: "0.8rem",
								borderRadius: "0.5rem",
							}}
						>
							<div
								style={{
									border: "1px solid #ddd",
									display: "flex",
									alignItems: "center",
									borderRadius: "0.25rem",
									overflow: "hidden",
									cursor: "pointer",
								}}
							>
								<div
									style={{
										height: "1rem",
										width: "0.7rem",
										backgroundColor: palette.pageText,
									}}
								/>
								<div
									style={{
										height: "1rem",
										width: "0.7rem",
										backgroundColor: palette.pageBg,
									}}
								/>
								<div
									style={{
										height: "1rem",
										width: "0.7rem",
										backgroundColor:
											palette.productSectionBg,
									}}
								/>
							</div>

							<p
								style={{
									flexGrow: 1,
									fontSize: "0.85rem",
									fontWeight: "500",
								}}
							>
								{t("kopy_pages.default_theme_colors")}
								{isAutostoreDefaultSelected && (
									<span
										style={{
											marginLeft: "0.3rem",
											fontSize: "0.75rem",
											fontWeight: "400",
											color: "#666",
										}}
									>
										({t("kopy_pages.current")})
									</span>
								)}
							</p>

							<div
								style={{
									flexShrink: 0,
									width: "0.5rem",
									height: "0.5rem",
									backgroundColor: isAutostoreDefaultSelected
										? "#5371ff"
										: "transparent",
									borderRadius: "50%",
								}}
							></div>
						</div>
					) : null}
					{Object.entries(themeColorPalettes).map(([key, value]) => {
						const selected = colorPalette === key
						const isCurrent =
							!showAutostoreDefaultOption &&
							themeCurrentPalette === key
						return (
							<div
								key={key}
								className="theme-color-palette-option"
								onClick={() => onPaletteSelected(key)}
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.5rem",
									cursor: "pointer",
									padding: "0.2rem",
									paddingRight: "0.8rem",
									borderRadius: "0.5rem",
								}}
							>
								<div
									style={{
										border: "1px solid #ddd",
										display: "flex",
										alignItems: "center",
										borderRadius: "0.25rem",
										overflow: "hidden",
										cursor: "pointer",
									}}
								>
									<div
										style={{
											height: "1rem",
											width: "0.7rem",
											backgroundColor: value.pageText,
										}}
									/>
									<div
										style={{
											height: "1rem",
											width: "0.7rem",
											backgroundColor: value.pageBg,
										}}
									/>
									<div
										style={{
											height: "1rem",
											width: "0.7rem",
											backgroundColor:
												value.productSectionBg,
										}}
									/>
								</div>

								<p
									style={{
										flexGrow: 1,
										fontSize: "0.85rem",
										fontWeight: "500",
										textTransform: "capitalize",
									}}
								>
									{key}
									{isCurrent && (
										<span
											style={{
												marginLeft: "0.3rem",
												fontSize: "0.75rem",
												fontWeight: "400",
												color: "#666",
											}}
										>
											({t("kopy_pages.current")})
										</span>
									)}
								</p>

								<div
									style={{
										flexShrink: 0,
										width: "0.5rem",
										height: "0.5rem",
										backgroundColor: selected
											? "#5371ff"
											: "transparent",
										borderRadius: "50%",
									}}
								></div>
							</div>
						)
					})}
				</div>
			</Popover>
		</>
	)
}

export const themeColorPalettes = {
	pink: {
		pageBg: "#ffe7ec",
		pageText: "#ff7e8d",
		pageTextSemiTransparent: "#ff7e8d50",
		productSectionBg: "#FEF5F6",
		textGradient:
			"linear-gradient(94deg, rgba(249, 84, 84, 1), rgba(255, 123, 123, 1) 33.33%, rgba(242, 65, 107, 1) 66.67%, rgba(245, 86, 0, 1) 100%)",
	},
	monochrome: {
		pageBg: "#e0e0e0",
		pageText: "#000000",
		pageTextSemiTransparent: "#00000050",
		productSectionBg: "#ffffff",
		textGradient:
			"linear-gradient(94deg, rgba(0, 0, 0, 1), rgba(64, 64, 64, 1) 33.33%, rgba(32, 32, 32, 1) 66.67%, rgba(48, 48, 48, 1) 100%)",
	},
	turquoise: {
		pageBg: "#c2fcff",
		pageText: "#0e7490",
		pageTextSemiTransparent: "#0e749050",
		productSectionBg: "#ecfeff",
		textGradient:
			"linear-gradient(94deg, rgba(14, 116, 144, 1), rgba(6, 182, 212, 1) 33.33%, rgba(8, 145, 178, 1) 66.67%, rgba(21, 94, 117, 1) 100%)",
	},
	purple: {
		pageBg: "#e0c2ff",
		pageText: "#7e22ce",
		pageTextSemiTransparent: "#7e22ce50",
		productSectionBg: "#faf5ff",
		textGradient:
			"linear-gradient(94deg, rgba(126, 34, 206, 1), rgba(168, 85, 247, 1) 33.33%, rgba(147, 51, 234, 1) 66.67%, rgba(192, 38, 211, 1) 100%)",
	},
	blue: {
		pageBg: "#c2dcff",
		pageText: "#1d4ed8",
		pageTextSemiTransparent: "#1d4ed850",
		productSectionBg: "#eff6ff",
		textGradient:
			"linear-gradient(94deg, rgba(29, 78, 216, 1), rgba(59, 130, 246, 1) 33.33%, rgba(37, 99, 235, 1) 66.67%, rgba(79, 70, 229, 1) 100%)",
	},
	green: {
		pageBg: "#c9f8d7",
		pageText: "#15803d",
		pageTextSemiTransparent: "#15803d50",
		productSectionBg: "#f0fdf4",
		textGradient:
			"linear-gradient(94deg, rgba(21, 128, 61, 1), rgba(34, 197, 94, 1) 33.33%, rgba(22, 163, 74, 1) 66.67%, rgba(5, 150, 105, 1) 100%)",
	},
	yellow: {
		pageBg: "#fdf8c4",
		pageText: "#a16207",
		pageTextSemiTransparent: "#a1620750",
		productSectionBg: "#fefce8",
		textGradient:
			"linear-gradient(94deg, rgba(161, 98, 7, 1), rgba(234, 179, 8, 1) 33.33%, rgba(202, 138, 4, 1) 66.67%, rgba(180, 83, 9, 1) 100%)",
	},
	orange: {
		pageBg: "#ffe3c2",
		pageText: "#c2410c",
		pageTextSemiTransparent: "#c2410c50",
		productSectionBg: "#fff7ed",
		textGradient:
			"linear-gradient(94deg, rgba(194, 65, 12, 1), rgba(249, 115, 22, 1) 33.33%, rgba(234, 88, 12, 1) 66.67%, rgba(220, 38, 38, 1) 100%)",
	},
	red: {
		pageBg: "#fbc6c6",
		pageText: "#b91c1c",
		pageTextSemiTransparent: "#b91c1c50",
		productSectionBg: "#fef2f2",
		textGradient:
			"linear-gradient(94deg, rgba(185, 28, 28, 1), rgba(239, 68, 68, 1) 33.33%, rgba(220, 38, 38, 1) 66.67%, rgba(190, 18, 60, 1) 100%)",
	},
	gray: {
		pageBg: "#dae0e7",
		pageText: "#4b5563",
		pageTextSemiTransparent: "#4b556350",
		productSectionBg: "#f9fafb",
		textGradient:
			"linear-gradient(94deg, rgba(75, 85, 99, 1), rgba(107, 114, 128, 1) 33.33%, rgba(55, 65, 81, 1) 66.67%, rgba(71, 85, 105, 1) 100%)",
	},
}
